import React, { useState } from 'react';
import { useMarkdown } from '../../hooks/useMarkdown';
import './ExportTools.css';

// 定义后端API的基础URL
const API_BASE_URL = 'https://toolsbackend.164567.xyz/api';

interface ExportToolsProps {
  className?: string;
}

const ExportTools: React.FC<ExportToolsProps> = ({ className = '' }) => {
  const { renderedMarkdown } = useMarkdown();
  const [isExporting, setIsExporting] = useState<string | null>(null);

  // 处理通用导出函数
  const handleExport = async (format: string, formatName: string) => {
    if (!renderedMarkdown) {
      alert('请先转换 Markdown 内容');
      return;
    }

    setIsExporting(format);

    try {
      // 获取当前时间戳作为文件名
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `exported-markdown-${timestamp}`;

      const formData = new FormData();
      formData.append('markdown', renderedMarkdown);
      formData.append('filename', filename);

      // 设置请求选项，指定需要返回blob
      const response = await fetch(`${API_BASE_URL}/convert/${format}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `导出${formatName}失败: ${response.statusText}`);
      }

      // 获取文件blob
      const blob = await response.blob();

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${format === 'docx-advanced' ? 'docx' : format}`;
      
      // 触发下载
      document.body.appendChild(a);
      a.click();
      
      // 清理
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 0);

      console.log(`已成功导出 ${formatName} 文件`);
    } catch (error) {
      console.error(`导出错误:`, error);
      alert(`导出过程中发生错误: ${error.message}`);
    } finally {
      setIsExporting(null);
    }
  };

  // 导出 DOCX 文件
  const handleExportDocx = () => handleExport('docx', 'Word');

  // 导出高级 DOCX 文件（更好的公式支持）
  const handleExportDocxAdvanced = () => handleExport('docx-advanced', '高级Word');

  // 导出为 HTML 文件
  const handleExportHtml = () => handleExport('html', 'HTML');

  // 导出为 PDF 文件
  const handleExportPdf = () => handleExport('pdf', 'PDF');

  return (
    <div className={`export-tools ${className}`}>
      <h3>导出工具</h3>
      <div className="export-buttons">
        <button 
          onClick={handleExportDocx}
          className={`export-btn docx-btn ${isExporting === 'docx' ? 'exporting' : ''}`}
          title="导出为Word文档(.docx)"
          disabled={!renderedMarkdown || !!isExporting}
        >
          {isExporting === 'docx' ? '正在导出...' : '导出为Word(.docx)'}
        </button>
        
        <button 
          onClick={handleExportDocxAdvanced}
          className={`export-btn docx-advanced-btn ${isExporting === 'docx-advanced' ? 'exporting' : ''}`}
          title="导出为Word文档(.docx)，更好的公式支持"
          disabled={!renderedMarkdown || !!isExporting}
        >
          {isExporting === 'docx-advanced' ? '正在导出...' : '导出高级Word(.docx)'}
        </button>
        
        <button 
          onClick={handleExportHtml}
          className={`export-btn html-btn ${isExporting === 'html' ? 'exporting' : ''}`}
          title="导出为HTML文件"
          disabled={!renderedMarkdown || !!isExporting}
        >
          {isExporting === 'html' ? '正在导出...' : '导出为HTML'}
        </button>
        
        <button 
          onClick={handleExportPdf}
          className={`export-btn pdf-btn ${isExporting === 'pdf' ? 'exporting' : ''}`}
          title="导出为PDF文件"
          disabled={!renderedMarkdown || !!isExporting}
        >
          {isExporting === 'pdf' ? '正在导出...' : '导出为PDF'}
        </button>
      </div>
      
      <div className="export-tips">
        <small>提示: 文件导出由后端服务处理，请耐心等待</small>
        <br/>
        <small>PDF导出使用LaTeX引擎，可能需要更长时间</small>
      </div>
    </div>
  );
};

export default ExportTools;